import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { apiFetch } from '../../api';
import { useImpersonation } from '../../context/ImpersonationContext';
import { useToast } from '../../components/Toast';

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatValue(val, band) {
  if (val) return '€' + val.toLocaleString();
  if (band && band !== 'unknown') return band.charAt(0).toUpperCase() + band.slice(1);
  return '—';
}

const TIERS = [
  { value: 'free',  label: 'Free' },
  { value: 'tier1', label: 'Starter (€19/mo)' },
  { value: 'tier2', label: 'Professional (€44/mo)' },
  { value: 'tier3', label: 'Scale (€69/mo)' },
];

const STATUSES = [
  { value: 'active',    label: 'Active' },
  { value: 'trialing',  label: 'Trialing' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'expired',   label: 'Expired' },
  { value: 'past_due',  label: 'Past due' },
];

const s = {
  page: { maxWidth: 900, margin: '32px auto', padding: '0 24px 60px' },
  backBtn: {
    background: 'none', border: 'none', color: '#64748b', fontSize: 13, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 6, padding: 0, marginBottom: 20,
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 },
  heading: { fontSize: 22, fontWeight: 700, color: '#0f172a', marginBottom: 2 },
  headingMeta: { display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 },
  metaItem: { fontSize: 13, color: '#64748b' },
  impersonateBtn: {
    background: '#d97706', color: '#fff', border: 'none',
    fontWeight: 700, fontSize: 13, padding: '9px 18px', borderRadius: 8, cursor: 'pointer',
    display: 'flex', alignItems: 'center', gap: 7,
  },
  section: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '20px 24px', marginBottom: 20 },
  sectionTitle: { fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 16 },
  actionsGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 },
  formGroup: { marginBottom: 0 },
  formLabel: { fontSize: 12, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 6, display: 'block' },
  select: {
    width: '100%', padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 7,
    fontSize: 13, color: '#374151', background: '#fff', cursor: 'pointer',
  },
  trialRow: { display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 },
  trialBtn: (color) => ({
    padding: '6px 14px', background: color, color: '#fff',
    border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer',
  }),
  saveBtn: {
    marginTop: 16, padding: '8px 20px', background: '#2563eb', color: '#fff',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  savingBtn: {
    marginTop: 16, padding: '8px 20px', background: '#93c5fd', color: '#fff',
    border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'not-allowed',
  },
  dangerZone: { borderTop: '1px solid #fee2e2', marginTop: 16, paddingTop: 16 },
  deleteBtn: {
    padding: '8px 16px', background: '#fff', color: '#dc2626',
    border: '1px solid #fecaca', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer',
  },
  adminRow: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 },
  profileGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 24px' },
  profileRow: { fontSize: 13 },
  profileKey: { color: '#94a3b8', marginRight: 6 },
  profileVal: { color: '#1e293b', fontWeight: 500 },
  matchCard: {
    border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 10,
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12,
  },
  matchTitle: { fontSize: 14, fontWeight: 600, color: '#0f172a', marginBottom: 4 },
  matchMeta: { fontSize: 12, color: '#64748b' },
  matchReasoning: { fontSize: 12, color: '#374151', marginTop: 6, lineHeight: 1.5 },
  verdictBadge: (verdict) => {
    const cfg = { great: ['#dcfce7', '#15803d', 'Great fit'], good: ['#fef9c3', '#854d0e', 'Good fit'], maybe: ['#f1f5f9', '#475569', 'Maybe'] };
    const [bg, color] = cfg[verdict] || cfg.maybe;
    return { background: bg, color, fontWeight: 700, fontSize: 13, padding: '4px 10px', borderRadius: 20, whiteSpace: 'nowrap', flexShrink: 0 };
  },
  keyRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '8px 12px', borderBottom: '1px solid #f1f5f9', fontSize: 13,
  },
  keyMono: { fontFamily: 'monospace', color: '#374151' },
  keySub: { fontSize: 12, color: '#94a3b8', marginTop: 2 },
  badge: (bg, color) => ({
    fontSize: 11, fontWeight: 600, padding: '2px 7px', borderRadius: 12,
    background: bg, color,
  }),
  empty: { fontSize: 13, color: '#94a3b8', fontStyle: 'italic' },
};

export default function AdminUserDetail() {
  const { id } = useParams();
  const { state } = useLocation();
  const navigate = useNavigate();
  const { startImpersonating } = useImpersonation();
  const showToast = useToast();

  const [profiles, setProfiles] = useState([]);
  const [matches, setMatches] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhooks, setWebhooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [variants, setVariants] = useState({});
  const [loadingVariants, setLoadingVariants] = useState({});

  // Editable account fields (pre-populated from navigation state)
  const [tier, setTier] = useState(state?.tier || 'free');
  const [status, setStatus] = useState(state?.status || 'active');
  const [isAdmin, setIsAdmin] = useState(state?.is_admin || false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const email = state?.email || id;

  useEffect(() => {
    Promise.all([
      apiFetch(`/admin/users/${id}/profiles`).catch(() => []),
      apiFetch(`/admin/users/${id}/matches`).catch(() => []),
      apiFetch(`/admin/users/${id}/api-keys`).catch(() => []),
      apiFetch(`/admin/users/${id}/webhooks`).catch(() => []),
    ]).then(([p, m, k, w]) => {
      setProfiles(Array.isArray(p) ? p : []);
      setMatches(m || []);
      setApiKeys(k || []);
      setWebhooks(w || []);
    }).finally(() => setLoading(false));
  }, [id]);

  async function toggleVariants(profileId) {
    if (variants[profileId]) {
      setVariants((v) => ({ ...v, [profileId]: null }));
      return;
    }
    setLoadingVariants((l) => ({ ...l, [profileId]: true }));
    try {
      const data = await apiFetch(`/admin/profiles/${profileId}/variants`);
      setVariants((v) => ({ ...v, [profileId]: data }));
    } catch {
      setVariants((v) => ({ ...v, [profileId]: [] }));
    } finally {
      setLoadingVariants((l) => ({ ...l, [profileId]: false }));
    }
  }

  function handleImpersonate() {
    startImpersonating({ id, email });
    navigate('/dashboard');
  }

  async function handleSave() {
    setSaving(true);
    try {
      await apiFetch(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ subscription_tier: tier, subscription_status: status, is_admin: isAdmin }),
      });
      showToast('Account updated', 'success');
    } catch (e) {
      showToast(e.message, 'error');
    } finally {
      setSaving(false);
    }
  }

  async function extendTrial(days) {
    const newEnd = new Date(Date.now() + days * 86400000).toISOString();
    try {
      await apiFetch(`/admin/users/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ trial_ends_at: newEnd, subscription_status: 'trialing' }),
      });
      showToast(`Trial extended by ${days} days`, 'success');
    } catch (e) {
      showToast(e.message, 'error');
    }
  }

  async function handleDelete() {
    if (!window.confirm(`Permanently delete ${email}? This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await apiFetch(`/admin/users/${id}`, { method: 'DELETE' });
      showToast('Account deleted', 'info');
      navigate('/admin/users');
    } catch (e) {
      showToast(e.message, 'error');
      setDeleting(false);
    }
  }

  if (loading) {
    return <div style={s.page}><p style={{ color: '#94a3b8', fontSize: 14 }}>Loading…</p></div>;
  }

  return (
    <div style={s.page}>
      <button style={s.backBtn} onClick={() => navigate('/admin/users')}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
          <path d="M19 12H5M12 5l-7 7 7 7"/>
        </svg>
        Back to users
      </button>

      {/* Header */}
      <div style={s.header}>
        <div>
          <div style={s.heading}>{email}</div>
          <div style={s.headingMeta}>
            <span style={s.metaItem}>{matches.length} matches</span>
            {state?.last_login_at && (
              <span style={s.metaItem}>Last login {formatDateTime(state.last_login_at)}</span>
            )}
          </div>
        </div>
        <button style={s.impersonateBtn} onClick={handleImpersonate}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
            <circle cx="12" cy="12" r="3"/>
          </svg>
          View as this user
        </button>
      </div>

      {/* Account actions */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Account</div>
        <div style={s.actionsGrid}>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Subscription tier</label>
            <select style={s.select} value={tier} onChange={(e) => setTier(e.target.value)}>
              {TIERS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
            </select>
          </div>
          <div style={s.formGroup}>
            <label style={s.formLabel}>Subscription status</label>
            <select style={s.select} value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUSES.map((st) => <option key={st.value} value={st.value}>{st.label}</option>)}
            </select>
          </div>
        </div>

        <div style={s.adminRow}>
          <input
            type="checkbox"
            id="is_admin_toggle"
            checked={isAdmin}
            onChange={(e) => setIsAdmin(e.target.checked)}
          />
          <label htmlFor="is_admin_toggle" style={{ fontSize: 13, color: '#374151', cursor: 'pointer' }}>
            Admin access
          </label>
        </div>

        <button style={saving ? s.savingBtn : s.saveBtn} onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>

        <div style={{ marginTop: 16 }}>
          <div style={s.formLabel}>Extend trial</div>
          <div style={s.trialRow}>
            {[7, 14, 30].map((d) => (
              <button key={d} style={s.trialBtn('#0891b2')} onClick={() => extendTrial(d)}>+{d} days</button>
            ))}
          </div>
        </div>

        <div style={s.dangerZone}>
          <button style={s.deleteBtn} onClick={handleDelete} disabled={deleting}>
            {deleting ? 'Deleting…' : 'Delete account'}
          </button>
          <span style={{ fontSize: 12, color: '#94a3b8', marginLeft: 12 }}>
            Permanently removes the user and all their data.
          </span>
        </div>
      </div>

      {/* Company profiles */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Profiles ({profiles.length})</div>
        {profiles.length === 0 ? (
          <p style={s.empty}>No profiles yet.</p>
        ) : profiles.map((p) => (
          <div key={p.id} style={{ border: '1px solid #e2e8f0', borderRadius: 8, padding: '12px 16px', marginBottom: 10 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontSize: 14, fontWeight: 600, color: '#0f172a' }}>{p.profile_name || p.company_name}</span>
                  {p.is_default && <span style={s.badge('#dbeafe', '#1d4ed8')}>default</span>}
                </div>
                <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>
                  {(p.procurement_types || []).length > 0
                    ? p.procurement_types.join(' · ')
                    : <em>no procurement types set</em>}
                </div>
                <div style={{ fontSize: 12, color: '#94a3b8' }}>
                  {p.synthetic_variant_count} synthetic vector{p.synthetic_variant_count !== 1 ? 's' : ''}
                  {' · '}updated {formatDate(p.updated_at)}
                </div>
              </div>
              <button
                onClick={() => toggleVariants(p.id)}
                style={{
                  background: 'none', border: '1px solid #e2e8f0', borderRadius: 6,
                  fontSize: 12, color: '#475569', padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap',
                }}
              >
                {loadingVariants[p.id] ? 'Loading…' : variants[p.id] ? 'Hide vectors' : 'View vectors'}
              </button>
            </div>

            {variants[p.id] && (
              <div style={{ marginTop: 12, borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                {variants[p.id].length === 0 ? (
                  <p style={s.empty}>No synthetic variants generated yet — save the profile to trigger generation.</p>
                ) : variants[p.id].map((v) => (
                  <div key={v.id} style={{ marginBottom: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#7c3aed', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 4 }}>
                      {v.procurement_type}
                    </div>
                    <div style={{ fontSize: 12, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap', background: '#f8fafc', borderRadius: 6, padding: '8px 12px' }}>
                      {v.synthetic_text}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* API Keys */}
      <div style={s.section}>
        <div style={s.sectionTitle}>API Keys ({apiKeys.length})</div>
        {apiKeys.length === 0 ? (
          <p style={s.empty}>No API keys.</p>
        ) : apiKeys.map((k) => (
          <div key={k.id} style={s.keyRow}>
            <div>
              <div style={s.keyMono}>{k.key_prefix}…{k.name ? ` — ${k.name}` : ''}</div>
              <div style={s.keySub}>
                Created {formatDate(k.created_at)}
                {k.last_used_at ? ` · Last used ${formatDate(k.last_used_at)}` : ' · Never used'}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Webhooks */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Webhooks ({webhooks.length})</div>
        {webhooks.length === 0 ? (
          <p style={s.empty}>No webhooks configured.</p>
        ) : webhooks.map((w) => (
          <div key={w.id} style={s.keyRow}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#1e293b' }}>{w.name || w.webhook_url}</span>
                <span style={s.badge(w.service === 'slack' ? '#dcfce7' : '#dbeafe', w.service === 'slack' ? '#15803d' : '#1d4ed8')}>
                  {w.service}
                </span>
                {!w.is_active && <span style={s.badge('#f1f5f9', '#94a3b8')}>paused</span>}
              </div>
              <div style={{ ...s.keySub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {w.webhook_url}{w.min_score > 0 ? ` · score ≥ ${w.min_score}` : ''}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Top matches */}
      <div style={s.section}>
        <div style={s.sectionTitle}>Top matches</div>
        {matches.length === 0 ? (
          <p style={s.empty}>No matches yet for this user.</p>
        ) : (
          matches.map((m, i) => (
            <div key={i} style={s.matchCard}>
              <div style={{ flex: 1 }}>
                <div style={s.matchTitle}>{m.tender?.title || 'Untitled'}</div>
                <div style={s.matchMeta}>
                  {m.tender?.country || '?'} · deadline {formatDate(m.tender?.deadline)} · {formatValue(m.tender?.contract_value, m.tender?.contract_size_band)}
                </div>
                {m.reasoning && <div style={s.matchReasoning}>{m.reasoning}</div>}
              </div>
              <div style={s.verdictBadge(m.verdict)}>{({ great: 'Great fit', good: 'Good fit', maybe: 'Maybe' })[m.verdict] || m.verdict}</div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
